import React, { useState, useEffect } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Page } from '../../layout';
import ResponsiveTabMenu from '../../shared/components/ResponsiveTabMenu';
import useUriFragment from '../../shared/hooks/useUriFragment';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import { isUserAdminTier, isUserOrgUnitTier } from '../../shared/services/checkUserTierService';
import {
  userLicensedOrgId,
  userLicensedOrgUnitId,
  userTier,
  userPrivileges
} from '../../../apollo/cache';
import { useTabExistCheck } from '../../shared/hooks/useTabExistCheck';
import * as LicenseTypes from '../constants/LicenseTypes';
import UserFreeLicensesTable from './freeLicenses/UserFreeLicensesTable';
import UserPaidLicensesTable from './paidLicenses/UserPaidLicensesTable';
import { getOrgSearchForUserManagement } from '../../shared/services/getUnitSearchPageUrlService';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { getActiveTabDisplayName } from '../../shared/services/getActiveTabDisplayNameService';
import { getPageTitle } from '../../shared/services/pageTitleService';
import { canUserAccessOrgName } from '../../shared/services/userAccessibilityService';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';
import ApolloError from '../../shared/components/ApolloError';
import GET_ORG_INFO from '../../shared/queries/getOrgInfoQuery';
import { PAGE_MODE } from '../constants/pageModeTypes';
import usePageMode from './shared/hooks/usePageMode';
import PrivilegeNames from '../../../constants/privilegeNames';
import UserDeactivatedLicensesTable from './deactivatedLicenses/UserDeactivatedLicensesTable';

const ACTIVE_TAB_TO_LICENSE_TABLE = {
  [LicenseTypes.FREE.value]: UserFreeLicensesTable,
  [LicenseTypes.PAID.value]: UserPaidLicensesTable,
  [LicenseTypes.DEACTIVATED.value]: UserDeactivatedLicensesTable
};

const UserManagementPage = () => {
  const location = useLocation();
  const { org } = useParams();
  const licensesModifiedSuccessfullyMessage = location.state
    ? location.state.licensesModifiedSuccessfullyMessage
    : null;

  const currentUserTier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);
  const allowedOrgId = useReactiveVar(userLicensedOrgId);
  const allowedOrgUnitId = useReactiveVar(userLicensedOrgUnitId);
  const [activeTabContent, setActiveTabContent] = useState(null);

  const orgId = isUserAdminTier(currentUserTier) ? parseInt(org) : allowedOrgId;
  const orgUnitId = isUserOrgUnitTier(currentUserTier) ? allowedOrgUnitId : null;

  const {
    data: orgInfo,
    error: orgInfoError,
    loading: orgInfoLoading
  } = useQuery(GET_ORG_INFO, {
    variables: { id: orgId },
    skip: !canUserAccessOrgName(currentUserTier)
  });

  const orgName = orgInfo && orgInfo.mddapOrganization ? orgInfo.mddapOrganization.Name : null;

  const pageMode = usePageMode(orgId);

  const tabs = getTabs(
    pageMode,
    privileges.includes(PrivilegeNames.UserManagement_ChangeLicense_DeactivatedLicenses)
  );
  const [activeTab, setActiveTab] = useUriFragment(tabs);

  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);
  const pageTitle = getPageTitle([orgName, activeTabDisplayName]);
  const annotationItems = orgName ? [orgName] : [];
  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: getOrgSearchForUserManagement(privileges)
    },
    { name: activeTabDisplayName, url: '' }
  ];

  useTabExistCheck(tabs, activeTab);

  const handleTabChange = type => {
    setActiveTab(type);
  };

  useEffect(() => {
    if ((canUserAccessOrgName(currentUserTier) && !orgInfo) || !pageMode) {
      setActiveTabContent(<SkeletonWrapper show inheritParentSize />);
      return;
    }

    const licensesTableProps = {
      licensesModifiedSuccessfullyMessage,
      orgId,
      orgName,
      orgUnitId,
      pageMode
    };

    const LicensesTable = ACTIVE_TAB_TO_LICENSE_TABLE[activeTab];
    setActiveTabContent(<LicensesTable {...licensesTableProps} />);
  }, [
    orgInfo,
    activeTab,
    pageMode,
    currentUserTier,
    orgId,
    orgName,
    orgUnitId,
    licensesModifiedSuccessfullyMessage
  ]);

  if (
    isNonAdminUserSpecificOrg(currentUserTier, org) ||
    isAdminUserForgotOrg(currentUserTier, org) ||
    isAdminUserEnterWrongOrg(orgInfo)
  ) {
    return <Navigate to='/page-not-found' />;
  }

  if (orgInfoError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgInfoLoading}
      />

      <div className='user-management-page'>
        <ResponsiveTabMenu activeTab={activeTab} tabs={tabs} onChange={handleTabChange} />
        {activeTabContent}
      </div>
    </Page>
  );
};

const getTabs = (pageMode, userHasAccessToDeactivedTab) => {
  const tabs = [LicenseTypes.PAID];

  if (pageMode !== PAGE_MODE.ISACAVipOrg) {
    tabs.push(LicenseTypes.FREE);
  }

  if (userHasAccessToDeactivedTab) {
    tabs.push(LicenseTypes.DEACTIVATED);
  }

  return tabs;
};

const isAdminUserForgotOrg = (currentUserTier, org) => {
  return isUserAdminTier(currentUserTier) && !org;
};

const isNonAdminUserSpecificOrg = (currentUserTier, org) => {
  return !isUserAdminTier(currentUserTier) && org;
};

const isAdminUserEnterWrongOrg = orgInfo => {
  return orgInfo && orgInfo.mddapOrganization === null;
};

export default UserManagementPage;
